import { useState, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';

export const qrSizes = [
  { label: '2cm x 2cm', value: 236 },
  { label: '3cm x 3cm', value: 354 },
  { label: '4cm x 4cm', value: 472 },
  { label: '5cm x 5cm', value: 590 },
] as const;

export const useQrCode = (tableNumber: string) => {
  const [customText, setCustomText] = useState('');
  const [selectedSize, setSelectedSize] = useState<number>(qrSizes[1].value);
  const qrRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleCustomTextChange = useCallback((text: string) => {
    setCustomText(text);
  }, []);

  const handleSizeChange = useCallback((size: number) => {
    setSelectedSize(size);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!qrRef.current) return;

    setIsDownloading(true);

    try {
      const svgElement = qrRef.current?.querySelector('svg');
      if (!svgElement) throw new Error('QR code not found');

      // First, load the logo
      const logoResponse = await fetch('/logo.svg');
      const logoBlob = await logoResponse.blob();
      const logoUrl = URL.createObjectURL(logoBlob);

      // Create a temporary image for the logo
      const logoImg = new Image();
      await new Promise((resolve, reject) => {
        logoImg.onload = resolve;
        logoImg.onerror = reject;
        logoImg.src = logoUrl;
      });

      // Now handle the QR code SVG
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], {
        type: 'image/svg+xml;charset=utf-8',
      });
      const svgUrl = URL.createObjectURL(svgBlob);

      // Create image from SVG
      const qrImg = new Image();
      await new Promise((resolve, reject) => {
        qrImg.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          canvas.width = qrImg.width;
          canvas.height = qrImg.height;

          // Draw white background
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw the QR code
          ctx.drawImage(qrImg, 0, 0);

          // Calculate logo position and size
          const svgViewBoxSize = 37;
          const logoSize = 7.4;
          const logoX = 14.8;
          const logoY = 14.8;

          const scale = canvas.width / svgViewBoxSize;
          const logoWidth = logoSize * scale;
          const logoHeight = logoSize * scale;
          const logoCanvasX = logoX * scale;
          const logoCanvasY = logoY * scale;

          // Draw the logo
          ctx.drawImage(
            logoImg,
            logoCanvasX,
            logoCanvasY,
            logoWidth,
            logoHeight
          );

          // Create download link
          const link = document.createElement('a');
          link.download = `table-${tableNumber}-qr${customText ? `-${customText}` : ''}.png`;
          link.href = canvas.toDataURL('image/png', 1.0);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          URL.revokeObjectURL(svgUrl);
          URL.revokeObjectURL(logoUrl);
          resolve(true);
        };
        qrImg.onerror = reject;
        qrImg.src = svgUrl;
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [tableNumber, customText, qrRef]);

  const handlePrint = useReactToPrint({
    documentTitle: `table-${tableNumber}-qr${customText ? `-${customText}` : ''}`,
    pageStyle: `
      @media print {
        body { margin: 0; padding: 16px; }
        @page { size: auto; margin: 20mm; }
      }
    `,
    contentRef: qrRef,
  });

  return {
    customText,
    selectedSize,
    qrRef,
    isDownloading,
    handleCustomTextChange,
    handleSizeChange,
    handleDownload,
    handlePrint,
  };
};
